import * as THREE from "three";
import * as TWEEN from "@tweenjs/tween.js";

export default function _setObject3DApi(object) {
  const tempVector = new THREE.Vector3();
  const tempQuaternion = new THREE.Quaternion();
  const xAxis = new THREE.Vector3(1, 0, 0);
  const yAxis = new THREE.Vector3(0, 1, 0);
  const zAxis = new THREE.Vector3(0, 0, 1);

  object.go = (dx, dy, dz) => {
    if (!object.userData.playInfo.isAlive) return;
    if (object.body && object.body.getCollisionFlags() !== 2) return;

    object.position.x += dx;
    object.position.y += dy;
    object.position.z += dz;

    if (object.body) object.body.needUpdate = true;
  };

  object.move = (dx, dy, dz, duration = 1) => {
    if (!object.userData.playInfo.isAlive) return;
    if (object.body && object.body.getCollisionFlags() !== 2) return;

    duration *= 1000;
    new TWEEN.Tween(object.position)
      .to({x: dx >= 0 ? `+${dx}` : `${dx}`, y: dy >= 0 ? `+${dy}` : `${dy}`, z: dz >= 0 ? `+${dz}` : `${dz}`}, duration)
      .onUpdate(() => {
        if (object.body) object.body.needUpdate = true;
      })
      .start();
  };

  object.rotate = (dx, dy, dz) => {
    if (!object.userData.playInfo.isAlive) return;
    if (object.body && object.body.getCollisionFlags() !== 2) return;

    dx *= Math.PI / 180;
    dy *= Math.PI / 180;
    dz *= Math.PI / 180;
    object.rotateOnAxis(xAxis, dx);
    object.rotateOnAxis(yAxis, dy);
    object.rotateOnAxis(zAxis, dz);
    if (object.body) object.body.needUpdate = true;
  };

  object.turn = (dx, dy, dz, duration = 1) => {
    if (!object.userData.playInfo.isAlive) return;
    if (object.body && object.body.getCollisionFlags() !== 2) return;

    dx *= Math.PI / 180;
    dy *= Math.PI / 180;
    dz *= Math.PI / 180;
    duration *= 1000;
    const angle = {currentValue: {dx: 0, dy: 0, dz: 0}, previousValue: {dx: 0, dy: 0, dz: 0}};
    new TWEEN.Tween(angle)
      .to({currentValue: {dx: dx, dy: dy, dz: dz}}, duration)
      .onUpdate(() => {
        if (dx) {
          object.quaternion.multiply(tempQuaternion.setFromAxisAngle(xAxis, angle.currentValue.dx - angle.previousValue.dx));
          angle.previousValue.dx = angle.currentValue.dx;
        }
        if (dy) {
          object.quaternion.multiply(tempQuaternion.setFromAxisAngle(yAxis, angle.currentValue.dy - angle.previousValue.dy));
          angle.previousValue.dy = angle.currentValue.dy;
        }
        if (dz) {
          object.quaternion.multiply(tempQuaternion.setFromAxisAngle(zAxis, angle.currentValue.dz - angle.previousValue.dz));
          angle.previousValue.dz = angle.currentValue.dz;
        }
        if (object.body) object.body.needUpdate = true;
      })
      .start();
  };

  object.onCollide = (target, callback, trigger = "start") => {
    if (!object.body) return;
    if (typeof target === "string") target = this.context.scenePlay.getObjectByProperty("title", target);
    object.body.onCollision((hit, event) => {
      if (hit === target && event === trigger) {
        if (!object.userData.playInfo.isAlive || !hit.userData.playInfo.isAlive) return;
        try {
          callback();
        } catch (error) {
          console.error(`${error.name} occurred in the onCollide function: ${error.message}`);
          window.alert(`${error.name} occurred in the onCollide function: ${error.message}`);
        }
      }
    });
  };

  object.setDynamic = boolean => {
    if (!object.body || !object.userData.playInfo.isAlive) return;
    if (boolean) {
      object.body.setCollisionFlags(0);
    } else {
      object.body.setCollisionFlags(2);
    }
  };

  object.applyForce = (x, y, z) => {
    if (!object.body || !object.userData.playInfo.isAlive) return;
    object.body.applyForce(x, y, z);
  };

  object.kill = () => {
    if (object.userData.playInfo.isAlive === false) return;
    object.userData.playInfo.isAlive = false;
    object.userData.playInfo.layerMask = object.layers.mask;
    object.traverse(child => {
      child.layers.disableAll();
      if (child.type === "Audio") child.stop();
    });
    if (object.body) {
      object.userData.playInfo.collisionFlag = object.body.getCollisionFlags();
      object.body.setCollisionFlags(5);
    }
  };

  object.revive = () => {
    if (object.userData.playInfo.isAlive === true) return;
    object.userData.playInfo.isAlive = true;
    object.traverse(child => (child.layers.mask = object.userData.playInfo.layerMask));
    if (object.body) {
      object.body.setCollisionFlags(object.userData.playInfo.collisionFlag);
    }
  };

  object.onClick = callback => {
    if (object.onClickObject === undefined) object.onClickObject = [];
    object.onClickObject.push(callback);
  };

  object.getAudio = () => {
    const audio = object.getObjectByProperty("type", "Audio");
    return audio;
  };

  object.cloneWithMethods = () => {
    const newObject = object.clone();
    _setObject3DApi(newObject);

    if (newObject.userData.objectScripts) {
      newObject.userData.objectScripts = {
        clientScripts: [...newObject.userData.objectScripts.clientScripts],
        serverScripts: [...newObject.userData.objectScripts.serverScripts],
      };
      // Remove object scripts from the cloned object
      newObject.userData.objectScripts.clientScripts.splice(0, newObject.userData.objectScripts.clientScripts.length);
      newObject.userData.objectScripts.serverScripts.splice(0, newObject.userData.objectScripts.serverScripts.length);
    }

    return newObject;
  };
}
